/**
 * ChatGPT Service
 * Handles communication with ChatGPT API
 */

import { loadConfig, getAiToken } from "@/lib/config";
import { apiClient } from "../client";
import { logUserPrompt } from "./chat-log.service";

interface ChatGPTMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

interface ChatGPTResponse {
  choices: Array<{
    message: {
      role: string;
      content: string;
    };
  }>;
}

interface ChatGPTResponseWithQuestions {
  response: string;
  relevantQuestions: string[];
}

/**
 * Parse ChatGPT response to extract relevant questions
 */
function parseChatGPTResponse(content: string): ChatGPTResponseWithQuestions {
  // Look for relevant questions section (format: "RELEVANT_QUESTIONS:" followed by questions)
  const relevantQuestionsMatch = content.match(/RELEVANT_QUESTIONS:\s*([\s\S]*?)(?:\n\n|\n$|$)/i);
  
  let relevantQuestions: string[] = [];
  let response = content;
  
  // Helper function to validate if a string is a valid question
  const isValidQuestion = (q: string): boolean => {
    const trimmed = q.trim();
    // Must have minimum length (at least 10 characters)
    if (trimmed.length < 10) return false;
    // Must end with a question mark
    if (!trimmed.endsWith('?')) return false;
    // Must contain actual text (not just special characters)
    if (!/[a-zA-Z0-9]/.test(trimmed)) return false;
    // Filter out single characters or just special characters
    if (/^[^a-zA-Z0-9]*$/.test(trimmed)) return false;
    // Filter out questions that are just "*" or similar
    if (/^[*•\-.\s]+$/.test(trimmed)) return false;
    return true;
  };

  if (relevantQuestionsMatch) {
    // Extract questions from the match
    const questionsText = relevantQuestionsMatch[1].trim();
    // Split by newlines and filter out empty lines and invalid questions
    relevantQuestions = questionsText
      .split('\n')
      .map(q => q.replace(/^[-•*]\s*/, '').trim())
      .filter(q => isValidQuestion(q));
    
    // Remove the relevant questions section from the response
    response = content.replace(/RELEVANT_QUESTIONS:[\s\S]*/i, '').trim();
  } else {
    // Try to extract questions from bullet points at the end
    const lines = content.split('\n');
    const questionLines: string[] = [];
    let foundQuestionSection = false;
    
    // Look for patterns like "You might also ask:" or "Related questions:"
    for (let i = lines.length - 1; i >= 0; i--) {
      const line = lines[i].trim();
      if (line.match(/^(you might also ask|related questions|suggested questions|you can ask):?$/i)) {
        foundQuestionSection = true;
        continue;
      }
      if (foundQuestionSection && line.match(/^[-•*]\s*.+\?$/)) {
        const question = line.replace(/^[-•*]\s*/, '').trim();
        if (isValidQuestion(question)) {
          questionLines.unshift(question);
        }
      } else if (foundQuestionSection && line.length === 0) {
        break;
      }
    }
    
    if (questionLines.length > 0) {
      relevantQuestions = questionLines;
      // Remove question section from response
      const questionStartIndex = content.toLowerCase().indexOf('you might also ask');
      if (questionStartIndex > -1) {
        response = content.substring(0, questionStartIndex).trim();
      }
    }
  }
  
  return {
    response,
    // Limit to 4 high-quality questions
    relevantQuestions: relevantQuestions.slice(0, 4).filter(q => isValidQuestion(q)),
  };
}

/**
 * Call ChatGPT API for general Pharmasoft-related questions (without TOON data)
 */
export async function callChatGPTGeneral(userQuery: string): Promise<ChatGPTResponseWithQuestions> {
  // Ensure config is loaded and get the full config
  let config = await loadConfig();
  
  // If token is not in config, try reloading
  if (!config?.aiToken) {
    console.warn("AI Token not found in cached config, reloading...");
    // Force reload by clearing the promise
    const freshConfig = await apiClient.loadConfig();
    config = freshConfig;
  }
  
  const token = config?.aiToken || getAiToken();
  
  if (!token) {
    console.error("Config loaded:", config);
    console.error("AI Token from getAiToken():", getAiToken());
    console.error("Full config object:", JSON.stringify(config, null, 2));
    throw new Error("AI Token not found in configuration. Please check config.json and ensure 'aiToken' field is present.");
  }

  // System prompt focused on Pharmasoft-related information only
  const systemPrompt = `You are a Professional PharmaSoft CS Mithra Assistant. Your role is to provide helpful information and assistance related to PharmaSoft Director CS software and pharmaceutical business management.

IMPORTANT GUIDELINES:
- Only answer questions related to PharmaSoft Director CS software, pharmaceutical business management, sales, inventory, collections, outstanding payments, dashboard analytics, and related business operations.
- If the question is not related to PharmaSoft or pharmaceutical business management, politely redirect the user to ask about PharmaSoft-related topics.
- Provide clear, concise, and friendly responses.
- Use bullet points, emojis, and formatting to make responses engaging and user-friendly.
- Focus on practical advice and information relevant to pharmaceutical business operations.
- Use Indian Rupees (₹) for currency references, not dollars.
- At the end of your response, add a section "RELEVANT_QUESTIONS:" followed by 3-5 relevant follow-up questions (one per line, each starting with "-") that the user might want to ask about PharmaSoft Director CS or pharmaceutical business management.`;

  const userPrompt = `User Query: "${userQuery}"

Please provide a helpful response related to PharmaSoft Director CS or pharmaceutical business management. If the question is not related to these topics, politely inform the user that you can only assist with PharmaSoft-related questions.

At the end, include 3-5 relevant follow-up questions in this format:
RELEVANT_QUESTIONS:
- Question 1?
- Question 2?
- Question 3?`;

  const messages: ChatGPTMessage[] = [
    { role: "system", content: systemPrompt },
    { role: "user", content: userPrompt },
  ];

  // Log the prompt being sent to ChatGPT
  console.log("📤 Sending general question to ChatGPT:", {
    userQuery,
    timestamp: new Date().toISOString(),
  });

  try {
    // Try OpenAI API format first
    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        model: "gpt-4o-mini",
        messages: messages,
        temperature: 0.7,
        max_tokens: 1000,
      }),
    });
    

    if (!response.ok) {
      // If OpenAI API fails, try custom endpoint (if token is for custom API)
      const errorData = await response.json().catch(() => ({}));
      
      // Log token information for debugging
      const tokenLength = token?.length || 0;
      const maskedToken = token 
        ? `${token.substring(0, 8)}...${token.substring(token.length - 4)}` 
        : "No token";
      console.error("🔴 OpenAI API Error - Token Info:", {
        tokenPresent: !!token,
        tokenLength,
        maskedToken,
        status: response.status,
        errorMessage: errorData.error?.message,
        timestamp: new Date().toISOString(),
      });
      
      throw new Error(errorData.error?.message || `API Error: ${response.status}`);
    }

    const data: ChatGPTResponse = await response.json();
    
    if (data.choices && data.choices.length > 0) {
      const aiResponse = data.choices[0].message.content;
      console.log("📥 Received from ChatGPT (General):", {
        responseLength: aiResponse.length,
        timestamp: new Date().toISOString(),
      });
      return parseChatGPTResponse(aiResponse);
    }

    throw new Error("No response from ChatGPT");
  } catch (error: any) {
    // If OpenAI API fails, try custom endpoint format
    try {
      const customResponse = await fetch("https://orderapi.pharmasoft.biz/api/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: messages,
        }),
      });

      if (!customResponse.ok) {
        throw new Error(`Custom API Error: ${customResponse.status}`);
      }

      const customData = await customResponse.json();
      
      // Handle different response formats
      let aiResponse: string | undefined;
      
      if (customData.choices && customData.choices.length > 0) {
        aiResponse = customData.choices[0].message.content;
      } else if (customData.message) {
        aiResponse = customData.message;
      } else if (typeof customData === "string") {
        aiResponse = customData;
      }

      if (aiResponse) {
        console.log("📥 Received from Custom ChatGPT API (General):", {
          responseLength: aiResponse.length,
          timestamp: new Date().toISOString(),
        });
        return parseChatGPTResponse(aiResponse);
      }

      throw new Error("Unexpected response format from custom API");
    } catch (customError: any) {
      console.error("ChatGPT API Error:", error, customError);
      throw new Error(
        `Failed to get ChatGPT response: ${error?.message || customError?.message || "Unknown error"}`
      );
    }
  }
}

/**
 * Call ChatGPT API with user query and TOON data
 */
export async function callChatGPT(
  userQuery: string,
  toonData: string,
  dashboardName: string
): Promise<ChatGPTResponseWithQuestions> {
  // Ensure config is loaded and get the full config
  let config = await loadConfig();
  
  // If token is not in config, try reloading
  if (!config?.aiToken) {
    console.warn("AI Token not found in cached config, reloading...");
    // Force reload by clearing the promise
    const freshConfig = await apiClient.loadConfig();
    config = freshConfig;
  }
  
  const token = config?.aiToken || getAiToken();
  
  if (!token) {
    console.error("Config loaded:", config);
    console.error("AI Token from getAiToken():", getAiToken());
    console.error("Full config object:", JSON.stringify(config, null, 2));
    throw new Error("AI Token not found in configuration. Please check config.json and ensure 'aiToken' field is present.");
  }

  // Prepare the prompt with user query and TOON data
 const systemPrompt = "You are a Profressional PharmaSoft CS Mithra Assistant, analyze and give summary according to user query from DATA.";

  const userPrompt = `User Query: "${userQuery}"
Data:
${toonData}

RESPONSE INSTRUCTIONS:
-give summary of the data in a concise way relevant to the user query, Dont give List of data, give summary in a concise way amount in Rupees not in dollars.
-give with designed bullet points and icons and make it more engaging and user friendly.
-Add emojis and make it more engaging and user friendly.
-Dont show summary text in the response, just give the summary in a concise way
-For every bullet point, add a relevant icon and make it more engaging.
-Must explain clearly and friendly manner according to the user query.
-At the end of your response, add a section "RELEVANT_QUESTIONS:" followed by 3-5 relevant follow-up questions (one per line, each starting with "-") that the user might want to ask about this dashboard data or PharmaSoft Director CS.`;
  const messages: ChatGPTMessage[] = [
    { role: "system", content: systemPrompt },
    { role: "user", content: userPrompt },
  ];

  // Log the prompt being sent to ChatGPT
  console.log("📤 Sending to ChatGPT:", {
    userQuery,
    dashboardName,
    toonDataLength: toonData.length,
    timestamp: new Date().toISOString(),
  });

  try {
    // Try OpenAI API format first
    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        model: "gpt-4o-mini",
        messages: messages,
        temperature: 0.7,
        max_tokens: 1000,
      }),
    });
    

    if (!response.ok) {
      // If OpenAI API fails, try custom endpoint (if token is for custom API)
      const errorData = await response.json().catch(() => ({}));
      
      // Log token information for debugging
      const tokenLength = token?.length || 0;
      const maskedToken = token 
        ? `${token.substring(0, 8)}...${token.substring(token.length - 4)}` 
        : "No token";
      console.error("🔴 OpenAI API Error - Token Info:", {
        tokenPresent: !!token,
        tokenLength,
        maskedToken,
        status: response.status,
        errorMessage: errorData.error?.message,
        timestamp: new Date().toISOString(),
      });
      
      throw new Error(errorData.error?.message || `API Error: ${response.status}`);
    }

    const data: ChatGPTResponse = await response.json();
    
    if (data.choices && data.choices.length > 0) {
      const aiResponse = data.choices[0].message.content;
      console.log("📥 Received from ChatGPT:", {
        responseLength: aiResponse.length,
        timestamp: new Date().toISOString(),
      });
      return parseChatGPTResponse(aiResponse);
    }

    throw new Error("No response from ChatGPT");
  } catch (error: any) {
    // If OpenAI API fails, try custom endpoint format
    try {
      const customResponse = await fetch("https://orderapi.pharmasoft.biz/api/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: messages,
        }),
      });

      if (!customResponse.ok) {
        throw new Error(`Custom API Error: ${customResponse.status}`);
      }

      const customData = await customResponse.json();
      
      // Handle different response formats
      let aiResponse: string | undefined;
      
      if (customData.choices && customData.choices.length > 0) {
        aiResponse = customData.choices[0].message.content;
      } else if (customData.message) {
        aiResponse = customData.message;
      } else if (typeof customData === "string") {
        aiResponse = customData;
      }

      if (aiResponse) {
        console.log("📥 Received from Custom ChatGPT API:", {
          responseLength: aiResponse.length,
          timestamp: new Date().toISOString(),
        });
        return parseChatGPTResponse(aiResponse);
      }

      throw new Error("Unexpected response format from custom API");
    } catch (customError: any) {
      console.error("ChatGPT API Error:", error, customError);
      throw new Error(
        `Failed to get ChatGPT response: ${error?.message || customError?.message || "Unknown error"}`
      );
    }
  }
}
